import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { FaEnvelope, FaArrowLeft, FaCheckCircle } from 'react-icons/fa';

const ForgotPassword = () => {
  const { i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error(i18n.language === 'fr' ? 'Veuillez entrer votre email' : 'Please enter your email');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`, {
        email: email.toLowerCase()
      });
      
      setEmailSent(true);
      toast.success(
        i18n.language === 'fr' 
          ? 'Email envoyé ! Vérifiez votre boîte de réception.' 
          : 'Email sent! Check your inbox.'
      );
    } catch (error) {
      // Always show success to prevent email enumeration
      setEmailSent(true);
    } finally {
      setLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0A2540] via-[#0d3a5c] to-[#0A2540] flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-2xl border-0">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <FaCheckCircle className="text-green-600 text-3xl" />
            </div>
            <CardTitle className="text-2xl text-[#0A2540]">
              {i18n.language === 'fr' ? 'Email envoyé !' : 'Email sent!'}
            </CardTitle>
            <CardDescription className="text-base mt-2">
              {i18n.language === 'fr' 
                ? 'Si cette adresse est associée à un compte, vous recevrez un lien de réinitialisation.' 
                : 'If this email is associated with an account, you will receive a reset link.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-gray-600 mb-6">
              {i18n.language === 'fr' 
                ? 'Vérifiez votre boîte de réception et vos spams.' 
                : 'Check your inbox and spam folder.'}
            </p>
            
            <div className="space-y-3">
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => setEmailSent(false)}
              >
                {i18n.language === 'fr' ? 'Renvoyer l\'email' : 'Resend email'}
              </Button>
              
              <Link to="/login">
                <Button className="w-full bg-[#0A2540] hover:bg-[#0d3a5c]">
                  <FaArrowLeft className="mr-2" />
                  {i18n.language === 'fr' ? 'Retour à la connexion' : 'Back to login'}
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A2540] via-[#0d3a5c] to-[#0A2540] flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-2xl border-0">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-16 h-16 bg-[#635BFF]/10 rounded-full flex items-center justify-center mb-4">
            <FaEnvelope className="text-[#635BFF] text-2xl" />
          </div>
          <CardTitle className="text-2xl text-[#0A2540]">
            {i18n.language === 'fr' ? 'Mot de passe oublié ?' : 'Forgot password?'}
          </CardTitle>
          <CardDescription className="text-base mt-2">
            {i18n.language === 'fr' 
              ? 'Entrez votre email pour recevoir un lien de réinitialisation.' 
              : 'Enter your email to receive a reset link.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">
                {i18n.language === 'fr' ? 'Adresse email' : 'Email address'}
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="exemple@email.com"
                className="mt-1"
                autoFocus
              />
            </div>
            
            <Button 
              type="submit" 
              className="w-full bg-[#635BFF] hover:bg-[#5248d9] py-6 text-lg"
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {i18n.language === 'fr' ? 'Envoi en cours...' : 'Sending...'}
                </span>
              ) : (
                i18n.language === 'fr' ? 'Envoyer le lien' : 'Send reset link'
              )}
            </Button>
          </form>
          
          <div className="mt-6 text-center">
            <Link 
              to="/login" 
              className="text-[#635BFF] hover:underline flex items-center justify-center"
            >
              <FaArrowLeft className="mr-2" />
              {i18n.language === 'fr' ? 'Retour à la connexion' : 'Back to login'}
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPassword;
